import axios from 'axios';

const TRIALS_BASE = 'https://clinicaltrials.gov/api/v2';

const SEARCH_TERMS = [
  'cancer', 'diabetes', 'depression', 'alzheimer',
  'obesity', 'heart failure', 'HIV', 'autoimmune'
];

async function fetchTrialsForTerm(term) {
  try {
    const url = `${TRIALS_BASE}/studies?query.cond=${encodeURIComponent(term)}&filter.overallStatus=RECRUITING&pageSize=40&fields=protocolSection`;
    const response = await axios.get(url);
    return response.data.studies || [];
  } catch {
    return [];
  }
}

export async function fetchPipeline() {
  try {
    const requests = SEARCH_TERMS.map(term => fetchTrialsForTerm(term));
    const results = await Promise.allSettled(requests);

    const allStudies = results
      .filter(r => r.status === 'fulfilled')
      .flatMap(r => r.value);

    const validPhases = ['PHASE2', 'PHASE3', 'PHASE4'];

    // Group by drug name
    const drugMap = {};

    allStudies.forEach(s => {
      const proto = s.protocolSection;
      const phase = proto?.designModule?.phases?.[0];
      if (!validPhases.includes(phase)) return;

      const enrollment = proto?.designModule?.enrollmentInfo?.count || 0;
      if (enrollment === 0) return;

      const conditions = proto?.conditionsModule?.conditions || [];
      const interventions = proto?.armsInterventionsModule?.interventions || [];
      const sponsor = proto?.sponsorCollaboratorsModule?.leadSponsor?.name || 'Unknown';
      const sponsorClass = proto?.sponsorCollaboratorsModule?.leadSponsor?.class || '';

      // Only use drug/biologic interventions
      const drugInterventions = interventions.filter(i =>
        i.type === 'DRUG' || i.type === 'BIOLOGICAL'
      );

      drugInterventions.forEach(intervention => {
        let rawName = intervention.name || '';
        // Skip placebo/control arms and very long names (likely descriptions not drug names)
        if (!rawName || rawName.length > 60) return;
        if (/placebo|saline|control|standard of care|usual care/i.test(rawName)) return;

        // Strip dosage/formulation suffixes so "Dapagliflozin 10 MG" and
        // "Dapagliflozin 5mg tablets" merge into one card and search cleanly
        rawName = rawName
          .replace(/\s+\d+(\.\d+)?\s*(mg|mcg|µg|ml|g|%|iu|units?)\b.*$/i, '')
          .replace(/\s+(tablets?|capsules?|injection|oral|topical|solution)\b.*$/i, '')
          .replace(/\s+(in|for|with|only product)$/i, '')
          .trim();
        if (rawName.length < 3) return;

        const key = rawName.toLowerCase();
        if (!drugMap[key]) {
          drugMap[key] = {
            name: rawName,
            phase,
            conditions: new Set(),
            totalEnrollment: 0,
            trialCount: 0,
            sponsor,
            industrySponsored: sponsorClass === 'INDUSTRY',
          };
        }

        const entry = drugMap[key];
        // Upgrade phase if higher
        const phaseOrder = { PHASE2: 1, PHASE3: 2, PHASE4: 3 };
        if ((phaseOrder[phase] || 0) > (phaseOrder[entry.phase] || 0)) {
          entry.phase = phase;
        }

        conditions.slice(0, 2).forEach(c => entry.conditions.add(c));
        entry.totalEnrollment += enrollment;
        entry.trialCount += 1;
      });
    });

    return Object.values(drugMap)
      .filter(d => d.trialCount >= 1 && d.totalEnrollment >= 100)
      .map(d => ({
        ...d,
        conditions: Array.from(d.conditions).slice(0, 3),
      }))
      .sort((a, b) => {
        // Phase 3/4 first, then by enrollment
        const phaseOrder = { PHASE4: 3, PHASE3: 2, PHASE2: 1 };
        const pDiff = (phaseOrder[b.phase] || 0) - (phaseOrder[a.phase] || 0);
        if (pDiff !== 0) return pDiff;
        return b.totalEnrollment - a.totalEnrollment;
      })
      .slice(0, 40);
  } catch (err) {
    console.error('Pipeline fetch error:', err.response?.data || err.message);
    throw err;
  }
}
